import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { listTenants, listServicesForTenant, compareSettings } from '../../services/envScanApi';
import { applyInject } from '../../services/envInjectorApi';

const formatServiceType = (s) =>
  (s || '').split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

/**
 * Loads CORS for many tenants of one service type, lets user check/uncheck origins per app,
 * previews the diff, and pushes changes via applyInject.
 */
const CorsManager = () => {
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [tenantsError, setTenantsError] = useState(null);

  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [selectedTenants, setSelectedTenants] = useState([]);

  const [results, setResults] = useState(null);   // [ScannedAppService]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [editMode, setEditMode] = useState(false);
  const [edits, setEdits] = useState({}); // { appName: Set<origin> }
  const [newOrigin, setNewOrigin] = useState('');
  const [pickedTargets, setPickedTargets] = useState(new Set()); // appNames to add new origin to
  const [pushLoading, setPushLoading] = useState(false);
  const [pushResults, setPushResults] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Bootstrap tenants
  useEffect(() => {
    let cancelled = false;
    setTenantsLoading(true);
    listTenants()
      .then((list) => {
        if (cancelled) return;
        setTenants(list || []);
        // default-select all by default
        setSelectedTenants(list || []);
      })
      .catch((err) => !cancelled && setTenantsError(err.message))
      .finally(() => !cancelled && setTenantsLoading(false));
    return () => { cancelled = true; };
  }, []);

  // Service type union when tenants change
  useEffect(() => {
    if (selectedTenants.length === 0) {
      setServices([]);
      setSelectedService('');
      return;
    }
    let cancelled = false;
    Promise.all(selectedTenants.map((t) => listServicesForTenant(t).catch(() => [])))
      .then((lists) => {
        if (cancelled) return;
        const union = Array.from(new Set(lists.flat())).sort();
        setServices(union);
        if (union.length > 0 && !union.includes(selectedService)) {
          setSelectedService(union[0]);
        }
      });
    return () => { cancelled = true; };
  }, [selectedTenants, selectedService]);

  const loadCors = useCallback(async () => {
    if (selectedTenants.length === 0 || !selectedService) return;
    setLoading(true);
    setError(null);
    setResults(null);
    setEditMode(false);
    setEdits({});
    setPushResults(null);
    setShowPreview(false);
    try {
      // Always show secrets=false. CORS values aren't sensitive but it keeps the response leaner.
      // Backend caps at 5 per call, so chunk if needed.
      const chunks = [];
      const cap = 5;
      for (let i = 0; i < selectedTenants.length; i += cap) {
        chunks.push(selectedTenants.slice(i, i + cap));
      }
      const responses = await Promise.all(
        chunks.map((c) => compareSettings(c, selectedService, false))
      );
      setResults(responses.flat());
    } catch (err) {
      setError(err.message || 'Failed to load CORS data');
    } finally {
      setLoading(false);
    }
  }, [selectedTenants, selectedService]);

  // Build per-app CORS view
  const corsData = useMemo(() => {
    if (!results) return null;
    const apps = {};      // appName -> { origins: Set, tenant, resourceGroup }
    const allOrigins = new Set();
    const appNames = [];

    for (const app of results) {
      const corsSetting = app.settings.find((s) => s.name.toLowerCase() === 'cors');
      const origins = new Set();
      if (corsSetting?.value) {
        for (const o of corsSetting.value.split('|')) {
          const t = o.trim();
          if (t) {
            origins.add(t);
            allOrigins.add(t);
          }
        }
      }
      apps[app.name] = { origins, tenant: app.tenantName, resourceGroup: app.resourceGroup };
      appNames.push(app.name);
    }

    const sortedOrigins = [...allOrigins].sort();
    return {
      apps,
      appNames,
      allOrigins: sortedOrigins,
      inconsistentCount: sortedOrigins.filter((o) => {
        const presentCount = appNames.filter((n) => apps[n].origins.has(o)).length;
        return presentCount > 0 && presentCount < appNames.length;
      }).length,
    };
  }, [results]);

  const initEdits = useCallback(() => {
    if (!corsData) return;
    const e = {};
    for (const [appName, info] of Object.entries(corsData.apps)) {
      e[appName] = new Set(info.origins);
    }
    setEdits(e);
    setPickedTargets(new Set());
    setNewOrigin('');
    setPushResults(null);
    setShowPreview(false);
  }, [corsData]);

  const changes = useMemo(() => {
    if (!corsData || !editMode) return [];
    const out = [];
    for (const appName of corsData.appNames) {
      const original = corsData.apps[appName].origins;
      const edited = edits[appName];
      if (!edited) continue;
      const added = [...edited].filter((o) => !original.has(o));
      const removed = [...original].filter((o) => !edited.has(o));
      if (added.length > 0 || removed.length > 0) {
        out.push({
          appName,
          tenant: corsData.apps[appName].tenant,
          resourceGroup: corsData.apps[appName].resourceGroup,
          added,
          removed,
          newValue: [...edited].join('|'),
        });
      }
    }
    return out;
  }, [corsData, edits, editMode]);

  const handleApply = useCallback(async () => {
    if (changes.length === 0) return;
    setPushLoading(true);
    setPushResults(null);
    try {
      const targets = changes.map((c) => ({
        appServiceName: c.appName,
        resourceGroup: c.resourceGroup,
      }));
      const perAppValues = {};
      const overrides = {};
      for (const c of changes) {
        perAppValues[c.appName] = c.newValue;
        overrides[c.appName] = true;
      }
      const out = await applyInject({
        targets,
        settingName: 'CORS',
        settingValue: '',
        slotSetting: true,
        perAppValues,
        overrides,
      });
      setPushResults(out);
    } catch (err) {
      setPushResults([{
        appServiceName: 'Error',
        success: false,
        message: err.message || 'Failed to apply CORS changes',
      }]);
    } finally {
      setPushLoading(false);
    }
  }, [changes]);

  const toggleTenant = (t) => {
    setSelectedTenants((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200">CORS Origins Manager</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Compare CORS allow-list across tenants for one service type, and push fixes in bulk.
        </p>
      </div>

      {tenantsError && (
        <div className="p-4 bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 rounded-lg mb-4">
          {tenantsError}
        </div>
      )}

      <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-5 mb-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Tenants ({selectedTenants.length}/{tenants.length})
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedTenants([...tenants])}
              className="text-xs text-blue-600 hover:underline"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={() => setSelectedTenants([])}
              className="text-xs text-gray-500 hover:underline"
            >
              Clear
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {tenantsLoading && <span className="text-sm text-gray-400">Loading…</span>}
            {tenants.map((t) => (
              <label
                key={t}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs cursor-pointer border ${
                  selectedTenants.includes(t)
                    ? 'bg-blue-50 dark:bg-blue-950 border-blue-300 text-blue-700 dark:text-blue-300'
                    : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600'
                }`}
              >
                <input
                  type="checkbox"
                  className="w-3 h-3"
                  checked={selectedTenants.includes(t)}
                  onChange={() => toggleTenant(t)}
                />
                {t}
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Service Type</label>
            <select
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              disabled={services.length === 0}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
            >
              {services.length === 0 && <option>(pick tenants first)</option>}
              {services.map((s) => (
                <option key={s} value={s}>{formatServiceType(s)}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={loadCors}
              disabled={selectedTenants.length === 0 || !selectedService || loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Loading…' : 'Load CORS'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 rounded-lg mb-4">
          {error}
        </div>
      )}

      {corsData && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between flex-wrap gap-2">
            <div>
              <span className="text-sm font-medium">
                {corsData.appNames.length} app service{corsData.appNames.length === 1 ? '' : 's'} · {corsData.allOrigins.length} unique origin{corsData.allOrigins.length === 1 ? '' : 's'}
              </span>
              {corsData.inconsistentCount > 0 ? (
                <span className="ml-2 inline-flex px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">
                  {corsData.inconsistentCount} inconsistent
                </span>
              ) : (
                <span className="ml-2 inline-flex px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">
                  All consistent
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {!editMode ? (
                <button
                  type="button"
                  onClick={() => { setEditMode(true); initEdits(); }}
                  className="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"
                >
                  Edit Mode
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => { setEditMode(false); setEdits({}); setShowPreview(false); }}
                    className="px-3 py-1.5 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium"
                  >
                    Cancel
                  </button>
                  {changes.length > 0 && !showPreview && (
                    <button
                      type="button"
                      onClick={() => setShowPreview(true)}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                    >
                      Preview ({changes.length})
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {editMode && (
            <div className="p-4 bg-amber-50 dark:bg-amber-950 border-b border-amber-200 dark:border-amber-800">
              <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-2">Add Origin to Selected Apps</h3>
              <div className="flex gap-2 flex-wrap items-end">
                <input
                  type="text"
                  placeholder="https://example.com"
                  value={newOrigin}
                  onChange={(e) => setNewOrigin(e.target.value)}
                  className="flex-1 min-w-[280px] px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800"
                />
                <button
                  type="button"
                  disabled={!newOrigin.trim() || pickedTargets.size === 0}
                  onClick={() => {
                    const o = newOrigin.trim();
                    const url = o.startsWith('http') ? o : `https://${o}`;
                    setEdits((prev) => {
                      const next = { ...prev };
                      for (const app of pickedTargets) {
                        next[app] = new Set([...(next[app] || []), url]);
                      }
                      return next;
                    });
                    setNewOrigin('');
                    setShowPreview(false);
                  }}
                  className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  Add to Selected ({pickedTargets.size})
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <button
                  type="button"
                  onClick={() => setPickedTargets(new Set(corsData.appNames))}
                  className="text-xs text-blue-600 hover:underline"
                >Select all</button>
                <button
                  type="button"
                  onClick={() => setPickedTargets(new Set())}
                  className="text-xs text-gray-500 hover:underline"
                >Deselect all</button>
                {corsData.appNames.map((appName) => (
                  <label key={appName} className="text-xs flex items-center gap-1 px-2 py-0.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded">
                    <input
                      type="checkbox"
                      checked={pickedTargets.has(appName)}
                      onChange={(e) => {
                        setPickedTargets((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(appName);
                          else next.delete(appName);
                          return next;
                        });
                      }}
                      className="w-3 h-3"
                    />
                    {corsData.apps[appName].tenant}
                  </label>
                ))}
              </div>
            </div>
          )}

          {showPreview && changes.length > 0 && (
            <div className="p-4 bg-blue-50 dark:bg-blue-950 border-b border-blue-200 dark:border-blue-800">
              <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">Preview ({changes.length} changes)</h3>
              <div className="space-y-1 mb-3">
                {changes.map((c) => (
                  <div key={c.appName} className="text-xs">
                    <strong className="font-mono">{c.tenant}</strong>
                    {c.added.length > 0 && (
                      <span className="ml-2">+ {c.added.map((o) => <span key={o} className="px-1 mx-0.5 rounded bg-green-100 text-green-800">{o}</span>)}</span>
                    )}
                    {c.removed.length > 0 && (
                      <span className="ml-2">− {c.removed.map((o) => <span key={o} className="px-1 mx-0.5 rounded bg-red-100 text-red-800 line-through">{o}</span>)}</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowPreview(false)}
                  className="px-3 py-1.5 bg-gray-200 dark:bg-slate-700 rounded-lg text-sm font-medium"
                >Back</button>
                <button
                  type="button"
                  onClick={handleApply}
                  disabled={pushLoading}
                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  {pushLoading ? 'Applying…' : `Apply ${changes.length} change${changes.length === 1 ? '' : 's'}`}
                </button>
              </div>
            </div>
          )}

          {pushResults && (
            <div className="p-4 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Push results</h3>
              {pushResults.map((r) => (
                <div key={r.appServiceName} className={`flex items-center gap-2 py-1 text-sm ${r.success ? 'text-green-700' : 'text-red-700'}`}>
                  <span>{r.success ? '✓' : '✗'}</span>
                  <span className="font-medium">{r.appServiceName}</span>
                  <span className="text-gray-500 dark:text-gray-400">— {r.message}</span>
                </div>
              ))}
              <button
                type="button"
                onClick={loadCors}
                className="mt-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                Re-load
              </button>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-slate-950">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-700 dark:text-gray-300 min-w-[280px]">Origin</th>
                  {corsData.appNames.map((n) => (
                    <th key={n} className="text-center px-3 py-2 font-medium text-gray-700 dark:text-gray-300 min-w-[100px]">
                      {corsData.apps[n].tenant}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(editMode
                  ? [...new Set([...corsData.allOrigins, ...corsData.appNames.flatMap((n) => [...(edits[n] || [])])])].sort()
                  : corsData.allOrigins
                ).map((origin) => {
                  const presentCount = corsData.appNames.filter((n) => {
                    const src = editMode ? edits[n] : corsData.apps[n].origins;
                    return src && src.has(origin);
                  }).length;
                  const inconsistent = presentCount > 0 && presentCount < corsData.appNames.length;
                  return (
                    <tr key={origin} className={`border-t border-gray-100 dark:border-slate-800 ${inconsistent ? 'bg-red-50/40 dark:bg-red-950/30' : ''}`}>
                      <td className="px-4 py-2 font-mono text-xs break-all">
                        {origin}
                        {inconsistent && (
                          <span className="ml-2 inline-block text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800">
                            {presentCount}/{corsData.appNames.length}
                          </span>
                        )}
                      </td>
                      {corsData.appNames.map((appName) => {
                        const has = editMode
                          ? edits[appName]?.has(origin)
                          : corsData.apps[appName].origins.has(origin);
                        if (editMode) {
                          return (
                            <td key={appName} className="px-3 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={!!has}
                                onChange={(e) => {
                                  setEdits((prev) => {
                                    const next = { ...prev };
                                    next[appName] = new Set(next[appName] || []);
                                    if (e.target.checked) next[appName].add(origin);
                                    else next[appName].delete(origin);
                                    return next;
                                  });
                                  setShowPreview(false);
                                }}
                                className="w-4 h-4"
                              />
                            </td>
                          );
                        }
                        return (
                          <td key={appName} className={`px-3 py-2 text-center font-bold ${has ? 'text-green-600' : 'text-red-500 bg-red-50/60 dark:bg-red-950/40'}`}>
                            {has ? '✓' : '✗'}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorsManager;
